/**
 * Bids Page
 *
 * Manage bids submitted on available projects.
 * PURELY PRESENTATIONAL - all data comes from hooks.
 *
 * DDD Compliance:
 * - Uses application service hooks for data (useBidList)
 * - No business logic or calculations in component
 * - Displays view models from application layer
 * - Handles loading, error, and empty states gracefully
 */

import { useState } from 'react';
import { PageHeader } from '@/components/service-provider/PageHeader';
import { useBidList } from '@/application/service-provider/hooks/useBidData';
import type { BidCardViewModel } from '@/application/service-provider/view-models/BidViewModel';
import { BidDetailModal } from '@/components/service-provider/BidDetailModal';

export function BidsPage() {
  // UI state (not business logic)
  const [activeFilters, setActiveFilters] = useState<{
    status?: string;
    dateRange?: string;
  }>({});
  const [selectedBidId, setSelectedBidId] = useState<string | undefined>();

  // Fetch bids with current filters
  const { data, isLoading, error } = useBidList(activeFilters);

  // Handler for filter changes
  const handleFilterChange = (filterType: 'status' | 'dateRange', value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Loading state
  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Bids"
          description="Manage your bids on available projects"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Bids' },
          ]}
        />
        <div className="space-y-6">
          <FiltersSkeleton />
          <BidListSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div>
        <PageHeader
          title="Bids"
          description="Manage your bids on available projects"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Bids' },
          ]}
        />
        <ErrorState message={error?.message || 'Failed to load bids'} />
      </div>
    );
  }

  const { bids, filters, summary, isEmpty, emptyStateMessage, emptyStateAction } = data;

  return (
    <div>
      <PageHeader
        title="Bids"
        description="Manage your bids on available projects"
        breadcrumbs={[
          { label: 'Dashboard', href: '/provider/dashboard' },
          { label: 'Bids' },
        ]}
        actions={[
          {
            label: 'Browse Jobs',
            variant: 'primary',
            onClick: () => (window.location.href = '/provider/dashboard?tab=available'),
          },
        ]}
      />

      <div className="space-y-6">
        {/* Filters */}
        <div className="card">
          <div className="card-content">
            {/* Status Filter Pills */}
            <div className="mb-4">
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Status
              </label>
              <div className="flex flex-wrap gap-2">
                {filters.statuses.map((status) => (
                  <FilterPill
                    key={status.value}
                    label={status.label}
                    count={status.count}
                    isActive={status.isActive}
                    onClick={() => handleFilterChange('status', status.value)}
                  />
                ))}
              </div>
            </div>

            {/* Date Range Dropdown */}
            <div>
              <label
                htmlFor="dateRange"
                className="text-sm font-medium text-neutral-700 mb-2 block"
              >
                Date Range
              </label>
              <select
                id="dateRange"
                value={activeFilters.dateRange || 'all'}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full md:w-64 px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {filters.dateRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600">{summary.displayText}</p>
        </div>

        {/* Bid List or Empty State */}
        {isEmpty ? (
          <EmptyState message={emptyStateMessage} action={emptyStateAction} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bids.map((bid) => (
              <BidCard
                key={bid.id}
                bid={bid}
                onViewDetails={() => setSelectedBidId(bid.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bid Detail Modal */}
      {selectedBidId && (
        <BidDetailModal
          bidId={selectedBidId}
          onClose={() => setSelectedBidId(undefined)}
        />
      )}
    </div>
  );
}

// Helper Components (purely presentational)

function FilterPill({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
        isActive
          ? 'bg-primary-500 text-white'
          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
      }`}
    >
      {label} ({count})
    </button>
  );
}

function BidCard({
  bid,
  onViewDetails,
}: {
  bid: BidCardViewModel;
  onViewDetails: () => void;
}) {
  return (
    <div className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={onViewDetails}>
      <div className="card-content">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-neutral-900 mb-1">
              {bid.projectName}
            </h3>
            <p className="text-xs text-neutral-500 font-mono">
              Plan: {bid.planDisplayId}
            </p>
          </div>
          <StatusBadge badge={bid.statusBadge} />
        </div>

        {/* Project Address */}
        {bid.projectAddress !== 'N/A' && (
          <div className="mb-3">
            <p className="text-sm text-neutral-600">{bid.projectAddress}</p>
          </div>
        )}

        {/* Pricing Summary */}
        <div className="mb-3 p-3 bg-neutral-50 rounded-lg">
          {bid.pricing.haulingRate && (
            <div className="mb-2">
              <p className="text-xs text-neutral-600">Hauling Rate</p>
              <p className="text-sm font-semibold text-neutral-900">
                {bid.pricing.haulingRate}
              </p>
            </div>
          )}
          {bid.pricing.containerRates.length > 0 && (
            <div>
              <p className="text-xs text-neutral-600 mb-1">Container Rates</p>
              <div className="flex flex-wrap gap-2">
                {bid.pricing.containerRates.map((rate) => (
                  <span
                    key={rate.size}
                    className="text-xs bg-white px-2 py-1 rounded border border-neutral-200"
                  >
                    {rate.size}: {rate.rate}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Service Description Preview */}
        {bid.serviceDescription && (
          <div className="mb-3">
            <p className="text-sm text-neutral-700 line-clamp-2">
              {bid.serviceDescription}
            </p>
          </div>
        )}

        {/* Response Status */}
        {bid.response && (
          <div
            className={`mb-3 p-2 rounded ${
              bid.response.status === 'accepted'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <p
              className={`text-xs font-medium ${
                bid.response.status === 'accepted' ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {bid.response.status === 'accepted' ? 'Bid Accepted!' : 'Bid Rejected'}
            </p>
            {bid.response.message && (
              <p className="text-xs text-neutral-600 mt-1">{bid.response.message}</p>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-neutral-200">
          <div>
            <p className="text-xs text-neutral-500">Submitted</p>
            <p className="text-sm font-medium text-neutral-700">{bid.submittedRelative}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
            className="btn-outline text-sm"
            disabled={!bid.canViewDetails}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({
  badge,
}: {
  badge: { label: string; color: string; icon: string };
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  }[badge.color] || 'bg-gray-100 text-gray-800';

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-medium ${colorClasses}`}
    >
      {badge.label}
    </span>
  );
}

function EmptyState({
  message,
  action,
}: {
  message: string;
  action?: { label: string; url: string };
}) {
  return (
    <div className="card">
      <div className="card-content text-center py-12">
        <svg
          className="w-16 h-16 text-neutral-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Bids Found</h3>
        <p className="text-neutral-600 mb-4">{message}</p>
        {action && (
          <a href={action.url} className="btn-primary inline-block">
            {action.label}
          </a>
        )}
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="card">
      <div className="card-content">
        <div className="text-center py-8">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            Unable to Load Bids
          </h3>
          <p className="text-sm text-neutral-600 mb-4">{message}</p>
          <button
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function FiltersSkeleton() {
  return (
    <div className="card">
      <div className="card-content animate-pulse">
        <div className="mb-4">
          <div className="h-4 bg-neutral-200 rounded w-16 mb-2"></div>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 w-24 bg-neutral-200 rounded-full"></div>
            ))}
          </div>
        </div>
        <div>
          <div className="h-4 bg-neutral-200 rounded w-20 mb-2"></div>
          <div className="h-10 bg-neutral-200 rounded w-64"></div>
        </div>
      </div>
    </div>
  );
}

function BidListSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="card">
          <div className="card-content">
            <div className="flex justify-between mb-3">
              <div className="flex-1">
                <div className="h-6 bg-neutral-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
              </div>
              <div className="h-6 w-20 bg-neutral-200 rounded-full"></div>
            </div>
            <div className="h-16 bg-neutral-100 rounded mb-3"></div>
            <div className="h-12 bg-neutral-100 rounded mb-3"></div>
            <div className="flex justify-between pt-3 border-t border-neutral-200">
              <div className="h-8 bg-neutral-200 rounded w-20"></div>
              <div className="h-8 bg-neutral-200 rounded w-24"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
